import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp, Loader2, Check, AlertCircle } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ChecklistFillItem } from './ChecklistFillItem';
import type { ChecklistRespostaValor } from '@portal-leo/shared';
import {
  computeSectionStatus,
  sectionStatusConfig,
  collectCurrentItemIds,
  filterValoresToCurrentItems,
} from '../utils/checklistStatus';

interface ChecklistFillModalProps {
  isOpen: boolean;
  onClose: () => void;
  checklistId: string;
  comissaoMembroId?: string;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function ChecklistFillModal({
  isOpen,
  onClose,
  checklistId,
  comissaoMembroId,
}: ChecklistFillModalProps) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'COORDENADOR_EVENTO');

  const utils = trpc.useUtils();

  const [valores, setValores] = useState<Record<string, ChecklistRespostaValor>>({});
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const isDirtyRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { data: checklist, isLoading: isLoadingChecklist } = trpc.checklists.getById.useQuery(
    { id: checklistId },
    { enabled: isOpen, staleTime: 0 }
  );

  const { data: resposta } = trpc.checklists.getResposta.useQuery(
    { checklistId, comissaoMembroId: comissaoMembroId! },
    { enabled: isOpen && !!comissaoMembroId, staleTime: 0 }
  );

  // Auto-save mutation (does NOT invalidate getResposta to avoid re-sync loop)
  const autoSaveMutation = trpc.checklists.saveResposta.useMutation({
    onSuccess: () => {
      setSaveStatus('saved');
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
      utils.eventos.getEventoDashboard.invalidate();
    },
    onError: (error) => {
      console.error('Erro ao auto-salvar:', error);
      setSaveStatus('error');
    },
  });

  // Manual save mutation (invalidates to update progress everywhere)
  const saveMutation = trpc.checklists.saveResposta.useMutation({
    onSuccess: () => {
      utils.checklists.getResposta.invalidate();
      utils.eventos.getEventoDashboard.invalidate();
    },
    onError: (error) => {
      console.error('Erro ao salvar resposta:', error);
      alert('Erro ao salvar: ' + error.message);
    },
  });

  const completeMutation = trpc.checklists.completeResposta.useMutation({
    onSuccess: () => {
      utils.checklists.getResposta.invalidate();
      onClose();
    },
    onError: (error) => {
      console.error('Erro ao finalizar resposta:', error);
      alert('Erro ao finalizar: ' + error.message);
    },
  });

  // Collect current item IDs from the checklist structure
  const currentItemIds = useMemo(
    () => (checklist ? collectCurrentItemIds(checklist.secoes) : new Set<string>()),
    [checklist],
  );

  // Sync resposta -> valores, filtering out obsolete IDs
  useEffect(() => {
    if (resposta?.valores && checklist) {
      const itemIds = collectCurrentItemIds(checklist.secoes);
      const filtered = filterValoresToCurrentItems(
        resposta.valores as Record<string, ChecklistRespostaValor>,
        itemIds,
      );
      setValores(filtered);
    }
  }, [resposta, checklist]);

  // Auto-save effect
  useEffect(() => {
    if (!isDirtyRef.current || !comissaoMembroId) return;

    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (!isDirtyRef.current) return;
      isDirtyRef.current = false;
      setSaveStatus('saving');
      clearTimeout(savedTimerRef.current);
      autoSaveMutation.mutate({
        checklistId,
        comissaoMembroId,
        valores,
      });
    }, 800);
  }, [valores, checklistId, comissaoMembroId]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(debounceTimerRef.current);
      clearTimeout(savedTimerRef.current);
    };
  }, []);

  const handleValueChange = useCallback((itemId: string, value: ChecklistRespostaValor) => {
    isDirtyRef.current = true;
    setValores((prev) => ({ ...prev, [itemId]: value }));
  }, []);

  const toggleSection = (sectionId: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleSaveDraft = () => {
    if (!comissaoMembroId) return;
    clearTimeout(debounceTimerRef.current);
    isDirtyRef.current = false;
    saveMutation.mutate({
      checklistId,
      comissaoMembroId,
      valores,
    });
  };

  const handleComplete = async () => {
    if (!comissaoMembroId) return;
    clearTimeout(debounceTimerRef.current);
    isDirtyRef.current = false;
    const result = await saveMutation.mutateAsync({
      checklistId,
      comissaoMembroId,
      valores,
    });
    await completeMutation.mutateAsync({ id: result.id });
  };

  const canSave = !!comissaoMembroId && canEdit;

  // Calculate progress using only current item IDs
  const totalItems = currentItemIds.size;
  const filledItems = useMemo(() => {
    let count = 0;
    for (const id of currentItemIds) {
      const val = valores[id];
      if (val?.valor !== null && val?.valor !== undefined) count++;
    }
    return count;
  }, [valores, currentItemIds]);
  const progressPercent = totalItems > 0 ? Math.round((filledItems / totalItems) * 100) : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={checklist?.nome || 'Preencher Checklist'} size="lg">
      {isLoadingChecklist ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Warning when no member */}
          {!canSave && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                Adicione um membro a esta comissao para salvar o progresso do checklist.
              </p>
            </div>
          )}

          {/* Progress bar */}
          <div className="bg-gray-100 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600">Progresso</span>
              <span className="text-sm font-medium text-gray-900">
                {filledItems}/{totalItems} itens ({progressPercent}%)
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {checklist?.secoes.map((secao) => (
              <div key={secao.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection(secao.id)}
                  className="flex items-center justify-between w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{secao.nome}</span>
                    {(() => {
                      const status = computeSectionStatus(secao.itens, valores);
                      const config = sectionStatusConfig[status];
                      return (
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
                          {config.label}
                        </span>
                      );
                    })()}
                  </div>
                  {collapsedSections.has(secao.id) ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  )}
                </button>

                {!collapsedSections.has(secao.id) && (
                  <div className="p-3 space-y-2 divide-y divide-gray-100">
                    {secao.itens.map((item) => (
                      <ChecklistFillItem
                        key={item.id}
                        item={{ ...item, descricao: item.descricao ?? undefined, dataPrazo: (item as any).dataPrazo }}
                        value={valores[item.id]}
                        onChange={(value) => handleValueChange(item.id, value)}
                        disabled={!canEdit}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {resposta?.completedAt ? 'Finalizado' : 'Rascunho'}
              </span>
              {saveStatus === 'saving' && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Salvando...
                </span>
              )}
              {saveStatus === 'saved' && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <Check className="h-3 w-3" />
                  Salvo
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="flex items-center gap-1 text-xs text-red-600">
                  <AlertCircle className="h-3 w-3" />
                  Erro ao salvar
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                isLoading={saveMutation.isPending}
                disabled={!canSave}
              >
                Salvar Rascunho
              </Button>
              <Button
                type="button"
                onClick={handleComplete}
                isLoading={completeMutation.isPending}
                disabled={!canSave || progressPercent < 100}
              >
                Finalizar
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
